// Client-side share-card image generator.
// Renders a 1080x1920 (Instagram Story format, also works cropped for X) PNG
// summarising a completed run or verified race finish.
//
// Usage:
//   const blob = await generateShareCard({
//     name: 'Alex Mercer',
//     distanceKm: 5.32,
//     durationSec: 1938,
//     paceSecPerKm: 364,
//     eventName: 'City Lights 10K',      // omitted for personal runs
//     dateISO: '2026-02-14T18:20:00Z',
//     verified: true,                     // small trophy badge
//     path: [{lat,lng},…],                // optional GPS polyline (drawn as mini-map trace)
//   });
//   // blob is a PNG. Ship to navigator.share() or trigger download.

const W = 1080;
const H = 1920;
const PAD = 80;
const PRIMARY = '#5B3FFF';   // RIVL brand purple
const ACCENT = '#FF5A00';    // signal orange
const BG_TOP = '#0A0A0F';
const BG_BOTTOM = '#1A1132';
const TEXT = '#FAFAFA';
const MUTED = '#A3A3A3';

function fmt2(n) { return String(n).padStart(2, '0'); }

function formatTime(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${fmt2(m)}:${fmt2(ss)}`;
  return `${m}:${fmt2(ss)}`;
}

function formatPace(sec) {
  if (!sec || !isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${fmt2(s)}`;
}

function formatDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Draw a stylised path (GPS trace) onto ctx within [x, y, w, h] rect.
function drawPathTrace(ctx, path, x, y, w, h) {
  if (!Array.isArray(path) || path.length < 2) return;
  const lats = path.map((p) => p.lat).filter((v) => typeof v === 'number');
  const lngs = path.map((p) => p.lng).filter((v) => typeof v === 'number');
  if (!lats.length || !lngs.length) return;
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const dLat = Math.max(0.0001, maxLat - minLat);
  const dLng = Math.max(0.0001, maxLng - minLng);
  // Preserve aspect ratio.
  const scale = Math.min(w / dLng, h / dLat) * 0.9;
  const cx = x + w / 2;
  const cy = y + h / 2;

  const toX = (lng) => cx + (lng - (minLng + maxLng) / 2) * scale;
  // NOTE: canvas y grows down; latitudes grow north, so invert.
  const toY = (lat) => cy - (lat - (minLat + maxLat) / 2) * scale;

  ctx.save();
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 8;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowColor = ACCENT;
  ctx.shadowBlur = 14;
  ctx.beginPath();
  path.forEach((p, i) => {
    if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return;
    const px = toX(p.lng);
    const py = toY(p.lat);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.stroke();
  ctx.restore();

  // Start dot
  ctx.save();
  ctx.fillStyle = '#22C55E';
  ctx.beginPath();
  ctx.arc(toX(path[0].lng), toY(path[0].lat), 14, 0, Math.PI * 2);
  ctx.fill();
  // End dot
  ctx.fillStyle = ACCENT;
  const last = path[path.length - 1];
  ctx.beginPath();
  ctx.arc(toX(last.lng), toY(last.lat), 14, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Draw the RIVL "R" mark (approximation of the SVG icon).
function drawRivlMark(ctx, x, y, size) {
  // Purple→magenta rounded square with a white R and an orange speed accent.
  const grad = ctx.createLinearGradient(x, y, x + size, y + size);
  grad.addColorStop(0, '#5B3FFF');
  grad.addColorStop(1, '#B333FF');
  ctx.save();
  ctx.fillStyle = grad;
  ctx.beginPath();
  const r = size * 0.22;
  ctx.roundRect(x, y, size, size, r);
  ctx.fill();

  // R letter
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `900 ${size * 0.72}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('R', x + size / 2, y + size / 2 + size * 0.03);

  // Speed accent underline
  ctx.fillStyle = ACCENT;
  ctx.fillRect(x + size * 0.15, y + size * 0.78, size * 0.55, size * 0.06);
  ctx.restore();
}

export async function generateShareCard({
  name = 'Runner',
  distanceKm = 0,
  durationSec = 0,
  paceSecPerKm,
  eventName,
  dateISO,
  verified = false,
  path,
}) {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ---- Background gradient ----
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, BG_TOP);
  bg.addColorStop(1, BG_BOTTOM);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Subtle noise (dots) — grain aesthetic
  ctx.save();
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = '#FFFFFF';
  for (let i = 0; i < 900; i++) {
    ctx.fillRect(Math.random() * W, Math.random() * H, 1.5, 1.5);
  }
  ctx.restore();

  // Big purple corner glow (top-right)
  const glow = ctx.createRadialGradient(W * 0.95, 120, 40, W * 0.95, 120, 800);
  glow.addColorStop(0, 'rgba(179, 51, 255, 0.35)');
  glow.addColorStop(1, 'rgba(179, 51, 255, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // ---- Header: brand mark + wordmark ----
  drawRivlMark(ctx, PAD, 100, 130);
  ctx.fillStyle = TEXT;
  ctx.font = '900 90px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('RIVL', PAD + 160, 108);
  ctx.fillStyle = ACCENT;
  ctx.fillText('.', PAD + 160 + ctx.measureText('RIVL').width + 4, 108);

  // Tagline under mark
  ctx.fillStyle = MUTED;
  ctx.font = '600 26px system-ui, -apple-system, sans-serif';
  ctx.fillText('RUN · VERIFIED', PAD + 160, 210);

  // ---- Category label ----
  ctx.fillStyle = ACCENT;
  ctx.font = '800 34px system-ui, -apple-system, sans-serif';
  ctx.letterSpacing = '4px';
  const category = eventName ? (verified ? 'VERIFIED FINISH' : 'RACE FINISH') : 'PERSONAL RUN';
  ctx.fillText(category, PAD, 340);

  // ---- Event / user name ----
  ctx.fillStyle = TEXT;
  ctx.font = '900 88px system-ui, -apple-system, sans-serif';
  const title = eventName || `${name}'s run`;
  const truncTitle = title.length > 22 ? title.slice(0, 21) + '…' : title;
  ctx.fillText(truncTitle, PAD, 400);

  // subtitle: runner name if we have an event
  if (eventName) {
    ctx.fillStyle = MUTED;
    ctx.font = '500 38px system-ui, -apple-system, sans-serif';
    ctx.fillText(name, PAD, 510);
  }

  // ---- Route trace (optional) ----
  const traceX = PAD;
  const traceY = 620;
  const traceW = W - PAD * 2;
  const traceH = 480;
  if (Array.isArray(path) && path.length > 1) {
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(traceX, traceY, traceW, traceH, 24);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    drawPathTrace(ctx, path, traceX, traceY, traceW, traceH);
  } else {
    // Bold metric card fallback (no route data)
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    ctx.beginPath();
    ctx.roundRect(traceX, traceY, traceW, traceH, 24);
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = MUTED;
    ctx.textAlign = 'center';
    ctx.font = '500 30px system-ui, -apple-system, sans-serif';
    ctx.fillText('No GPS trace recorded', W / 2, traceY + traceH / 2 - 20);
    ctx.font = '900 220px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = PRIMARY;
    ctx.fillText(distanceKm.toFixed(2), W / 2, traceY + traceH / 2 - 100);
    ctx.textAlign = 'left';
  }

  // ---- Big stats row ----
  const statsY = 1200;
  const statCol = (W - PAD * 2) / 3;

  function stat(label, value, col) {
    const cx = PAD + col * statCol + statCol / 2;
    ctx.textAlign = 'center';
    ctx.fillStyle = MUTED;
    ctx.font = '700 28px system-ui, -apple-system, sans-serif';
    ctx.fillText(label.toUpperCase(), cx, statsY);
    ctx.fillStyle = TEXT;
    ctx.font = '900 100px "SF Mono", "Menlo", monospace';
    ctx.fillText(value, cx, statsY + 50);
  }

  stat('Distance', distanceKm.toFixed(2), 0);
  stat('Time', formatTime(durationSec), 1);
  stat('Pace', formatPace(paceSecPerKm), 2);

  // Unit subtitles
  ctx.textAlign = 'center';
  ctx.fillStyle = MUTED;
  ctx.font = '500 22px system-ui, -apple-system, sans-serif';
  ctx.fillText('KM', PAD + 0 * statCol + statCol / 2, statsY + 170);
  ctx.fillText('MIN', PAD + 1 * statCol + statCol / 2, statsY + 170);
  ctx.fillText('/KM', PAD + 2 * statCol + statCol / 2, statsY + 170);

  // ---- Verified badge (bottom-left) ----
  if (verified) {
    ctx.save();
    ctx.textAlign = 'left';
    ctx.fillStyle = '#22C55E';
    ctx.beginPath();
    ctx.arc(PAD + 22, 1660, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0A0A0F';
    ctx.font = '900 24px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('✓', PAD + 22, 1660);
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#22C55E';
    ctx.font = '900 30px system-ui, -apple-system, sans-serif';
    ctx.fillText('VERIFIED FINISH', PAD + 60, 1647);
    ctx.restore();
  }

  // ---- Date bottom-right ----
  ctx.textAlign = 'right';
  ctx.fillStyle = MUTED;
  ctx.font = '500 28px system-ui, -apple-system, sans-serif';
  ctx.fillText(formatDate(dateISO), W - PAD, 1655);
  ctx.textAlign = 'left';

  // ---- Footer CTA ----
  ctx.textAlign = 'center';
  ctx.fillStyle = MUTED;
  ctx.font = '600 30px system-ui, -apple-system, sans-serif';
  ctx.fillText('Run, verified.', W / 2, 1770);
  ctx.fillStyle = TEXT;
  ctx.font = '900 44px system-ui, -apple-system, sans-serif';
  ctx.fillText('rivl.run', W / 2, 1815);

  // Bottom accent line
  ctx.fillStyle = ACCENT;
  ctx.fillRect(W / 2 - 60, 1880, 120, 6);

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.95));
}
